/**
 * System Pipeline Templates
 *
 * Pre-built pipeline stage configurations for common use cases.
 * Users can copy these templates to create their own pipelines.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 */

import type { PipelineTemplate } from './types';

// =============================================================================
// PIPELINE TEMPLATE IDs
// =============================================================================

export const PIPELINE_TEMPLATE_IDS = {
  SALES_PIPELINE: 'tpl-pipe-sales-pipeline',
  LOAN_ORIGINATION: 'tpl-pipe-loan-origination',
  PARTNER_ONBOARDING: 'tpl-pipe-partner-onboarding',
  DEAL_FLOW: 'tpl-pipe-deal-flow',
} as const;

// =============================================================================
// PIPELINE TEMPLATES
// =============================================================================

export const PIPELINE_TEMPLATES: PipelineTemplate[] = [
  // ---------------------------------------------------------------------------
  // Sales Pipeline
  // ---------------------------------------------------------------------------
  {
    id: PIPELINE_TEMPLATE_IDS.SALES_PIPELINE,
    name: 'Sales Pipeline',
    description:
      'A classic sales pipeline for tracking leads from initial contact to closed deal. Perfect for any B2B sales process.',
    category: 'sales',
    icon: 'trending_up',
    color: '#4CAF50',
    tags: ['sales', 'leads', 'deals', 'crm', 'conversion'],
    complexity: 'beginner',
    industryFit: ['general', 'real_estate', 'lending'],
    data: {
      entityTypes: ['Lead', 'Deal', 'Opportunity'],
      stages: [
        {
          order: 1,
          name: 'New Lead',
          description: 'Fresh leads that need initial qualification',
          color: '#90CAF9',
          icon: 'person_add',
          isTerminal: false,
          defaultSlaHours: 24,
          requiredFields: ['name', 'email'],
        },
        {
          order: 2,
          name: 'Contacted',
          description: 'Initial outreach has been made',
          color: '#64B5F6',
          icon: 'phone',
          isTerminal: false,
          defaultSlaHours: 48,
        },
        {
          order: 3,
          name: 'Qualified',
          description: 'Lead meets criteria and has expressed interest',
          color: '#42A5F5',
          icon: 'verified',
          isTerminal: false,
          defaultSlaHours: 72,
          requiredFields: ['budget', 'timeline'],
        },
        {
          order: 4,
          name: 'Proposal Sent',
          description: 'Proposal or quote has been delivered',
          color: '#2196F3',
          icon: 'description',
          isTerminal: false,
          defaultSlaHours: 120,
          requiredFields: ['proposalAmount'],
        },
        {
          order: 5,
          name: 'Negotiation',
          description: 'Active discussions on terms and pricing',
          color: '#1E88E5',
          icon: 'handshake',
          isTerminal: false,
          defaultSlaHours: 168,
        },
        {
          order: 6,
          name: 'Closed Won',
          description: 'Deal successfully closed',
          color: '#4CAF50',
          icon: 'celebration',
          isTerminal: true,
          isSuccess: true,
        },
        {
          order: 7,
          name: 'Closed Lost',
          description: 'Deal did not close',
          color: '#F44336',
          icon: 'close',
          isTerminal: true,
          isSuccess: false,
        },
      ],
      defaultSlaConfig: {
        warningThresholdPercent: 75,
        enableNotifications: true,
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Loan Origination Pipeline
  // ---------------------------------------------------------------------------
  {
    id: PIPELINE_TEMPLATE_IDS.LOAN_ORIGINATION,
    name: 'Loan Origination',
    description:
      'Complete loan origination workflow from application to funding. Includes all standard stages for mortgage and hard money lending.',
    category: 'lending',
    icon: 'account_balance',
    color: '#2196F3',
    tags: ['loan', 'origination', 'mortgage', 'lending', 'underwriting'],
    complexity: 'intermediate',
    industryFit: ['lending', 'mortgage'],
    data: {
      entityTypes: ['LoanApplication', 'Case'],
      stages: [
        {
          order: 1,
          name: 'Application Received',
          description: 'New loan application submitted',
          color: '#E3F2FD',
          icon: 'inbox',
          isTerminal: false,
          defaultSlaHours: 4,
          requiredFields: ['borrowerName', 'loanAmount', 'propertyAddress'],
        },
        {
          order: 2,
          name: 'Document Collection',
          description: 'Gathering required documentation from borrower',
          color: '#BBDEFB',
          icon: 'folder_open',
          isTerminal: false,
          defaultSlaHours: 72,
        },
        {
          order: 3,
          name: 'Processing',
          description: 'Loan package being prepared for underwriting',
          color: '#90CAF9',
          icon: 'settings',
          isTerminal: false,
          defaultSlaHours: 48,
        },
        {
          order: 4,
          name: 'Underwriting',
          description: 'Loan under review by underwriting team',
          color: '#64B5F6',
          icon: 'rate_review',
          isTerminal: false,
          defaultSlaHours: 48,
          requiredFields: ['creditScore', 'ltv', 'dscr'],
        },
        {
          order: 5,
          name: 'Conditional Approval',
          description: 'Approved with conditions that need to be satisfied',
          color: '#42A5F5',
          icon: 'pending_actions',
          isTerminal: false,
          defaultSlaHours: 72,
        },
        {
          order: 6,
          name: 'Clear to Close',
          description: 'All conditions met, ready for closing',
          color: '#2196F3',
          icon: 'check_circle',
          isTerminal: false,
          defaultSlaHours: 24,
        },
        {
          order: 7,
          name: 'Closing Scheduled',
          description: 'Closing date set, documents being prepared',
          color: '#1976D2',
          icon: 'event',
          isTerminal: false,
          defaultSlaHours: 48,
          requiredFields: ['closingDate', 'closingAgent'],
        },
        {
          order: 8,
          name: 'Funded',
          description: 'Loan successfully funded',
          color: '#4CAF50',
          icon: 'celebration',
          isTerminal: true,
          isSuccess: true,
        },
        {
          order: 9,
          name: 'Denied',
          description: 'Loan application denied',
          color: '#F44336',
          icon: 'block',
          isTerminal: true,
          isSuccess: false,
        },
        {
          order: 10,
          name: 'Withdrawn',
          description: 'Borrower withdrew application',
          color: '#9E9E9E',
          icon: 'undo',
          isTerminal: true,
          isSuccess: false,
        },
      ],
      defaultSlaConfig: {
        warningThresholdPercent: 80,
        enableNotifications: true,
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Partner Onboarding Pipeline
  // ---------------------------------------------------------------------------
  {
    id: PIPELINE_TEMPLATE_IDS.PARTNER_ONBOARDING,
    name: 'Partner Onboarding',
    description:
      'Streamlined workflow for onboarding referral partners, brokers, or vendors. From application to fully active.',
    category: 'partnerships',
    icon: 'groups',
    color: '#9C27B0',
    tags: ['partner', 'onboarding', 'referral', 'broker', 'vendor'],
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'real_estate', 'general'],
    data: {
      entityTypes: ['Partner', 'Vendor', 'Broker'],
      stages: [
        {
          order: 1,
          name: 'Application Submitted',
          description: 'Partner application received',
          color: '#F3E5F5',
          icon: 'assignment',
          isTerminal: false,
          defaultSlaHours: 24,
          requiredFields: ['companyName', 'contactName', 'email'],
        },
        {
          order: 2,
          name: 'Documents Required',
          description: 'Awaiting required documentation (license, W-9, etc.)',
          color: '#E1BEE7',
          icon: 'upload_file',
          isTerminal: false,
          defaultSlaHours: 168,
        },
        {
          order: 3,
          name: 'Under Review',
          description: 'Application and documents being reviewed',
          color: '#CE93D8',
          icon: 'search',
          isTerminal: false,
          defaultSlaHours: 48,
        },
        {
          order: 4,
          name: 'Agreement Pending',
          description: 'Partner agreement sent, awaiting signature',
          color: '#BA68C8',
          icon: 'draw',
          isTerminal: false,
          defaultSlaHours: 72,
          requiredFields: ['agreementSentAt'],
        },
        {
          order: 5,
          name: 'Active',
          description: 'Partner is fully onboarded and active',
          color: '#4CAF50',
          icon: 'verified_user',
          isTerminal: true,
          isSuccess: true,
        },
        {
          order: 6,
          name: 'Declined',
          description: 'Partner application was declined',
          color: '#F44336',
          icon: 'person_off',
          isTerminal: true,
          isSuccess: false,
        },
      ],
      defaultSlaConfig: {
        warningThresholdPercent: 75,
        enableNotifications: true,
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Deal Flow Pipeline
  // ---------------------------------------------------------------------------
  {
    id: PIPELINE_TEMPLATE_IDS.DEAL_FLOW,
    name: 'Deal Flow',
    description:
      'Track investment opportunities from sourcing to close. Ideal for fund managers, investment firms, or deal teams.',
    category: 'deals',
    icon: 'analytics',
    color: '#FF9800',
    tags: ['deals', 'investment', 'pipeline', 'opportunities', 'fund'],
    complexity: 'intermediate',
    industryFit: ['lending', 'fintech', 'real_estate'],
    data: {
      entityTypes: ['Deal', 'Investment', 'Opportunity'],
      stages: [
        {
          order: 1,
          name: 'Sourced',
          description: 'New deal opportunity identified',
          color: '#FFF3E0',
          icon: 'lightbulb',
          isTerminal: false,
          defaultSlaHours: 48,
        },
        {
          order: 2,
          name: 'Initial Review',
          description: 'High-level review of deal merits',
          color: '#FFE0B2',
          icon: 'preview',
          isTerminal: false,
          defaultSlaHours: 72,
        },
        {
          order: 3,
          name: 'Due Diligence',
          description: 'Deep-dive analysis and verification',
          color: '#FFCC80',
          icon: 'fact_check',
          isTerminal: false,
          defaultSlaHours: 168,
          requiredFields: ['dueDiligenceStarted'],
        },
        {
          order: 4,
          name: 'Committee Review',
          description: 'Investment committee decision pending',
          color: '#FFB74D',
          icon: 'groups',
          isTerminal: false,
          defaultSlaHours: 120,
        },
        {
          order: 5,
          name: 'Term Sheet Issued',
          description: 'Terms offered, awaiting acceptance',
          color: '#FFA726',
          icon: 'article',
          isTerminal: false,
          defaultSlaHours: 72,
          requiredFields: ['termSheetAmount'],
        },
        {
          order: 6,
          name: 'Closed',
          description: 'Deal successfully closed',
          color: '#4CAF50',
          icon: 'emoji_events',
          isTerminal: true,
          isSuccess: true,
        },
        {
          order: 7,
          name: 'Passed',
          description: 'Deal did not meet investment criteria',
          color: '#F44336',
          icon: 'do_not_disturb',
          isTerminal: true,
          isSuccess: false,
        },
      ],
      defaultSlaConfig: {
        warningThresholdPercent: 80,
        enableNotifications: true,
      },
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all pipeline templates
 */
export function getAllPipelineTemplates(): PipelineTemplate[] {
  return PIPELINE_TEMPLATES;
}

/**
 * Get pipeline template by ID
 */
export function getPipelineTemplateById(id: string): PipelineTemplate | undefined {
  return PIPELINE_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get pipeline templates by category
 */
export function getPipelineTemplatesByCategory(category: string): PipelineTemplate[] {
  return PIPELINE_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Search pipeline templates by name, description, or tags
 */
export function searchPipelineTemplates(query: string): PipelineTemplate[] {
  const lowerQuery = query.toLowerCase();
  return PIPELINE_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
